/**
 * The steps before the patient surface. First the patient's name (AUTH-22): an account made by
 * OTP sign-up has none, and doctors and pharmacists saw a blank name. Then the consents (ACCT-03):
 * a patient missing a required consent — a new account, or one whose Terms/Privacy version changed
 * — reviews and accepts it here first. What is required, and at which version, comes from the API
 * (GET /me/consents → needs_action); the ticked consents are recorded together
 * (POST /me/consents/batch). Optional ones start unticked. If a check itself fails the surface
 * opens anyway (the API refuses orders without consent when enforcement is on).
 */

import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useState } from "react";

import { ApiError, describeError } from "../../api/errors";
import { type ConsentStatus, grantConsents, listConsents } from "../../api/notifications";
import { getMe, patchProfile } from "../../api/patient";
import { useAuth } from "../../auth/AuthContext";
import { POLICIES } from "../../legal/policies";

const LABELS: Record<string, string> = {
  health_data_processing:
    "I consent to EMediExp processing my health data (prescriptions and orders) to provide care.",
  terms_of_service: "I accept the Terms of Service.",
  privacy_policy: "I accept the Privacy Policy.",
  data_sharing_pharmacy: "I allow sharing my prescriptions with partner pharmacies.",
  transactional_messaging: "I agree to receive SMS and WhatsApp updates about my orders.",
};
const POLICY_OF: Record<string, keyof typeof POLICIES> = { terms_of_service: "terms", privacy_policy: "privacy" };
// Transactional messages aren't offered: they are on until switched off in Settings (D-10).
const OPTIONAL_AT_SIGNUP = ["data_sharing_pharmacy"];
// The batch looks nothing up, so a 404 means the server doesn't have the route yet (ACCT-27):
// say so in words a patient can act on, not "Not Found".
const SAVE_UNAVAILABLE = "Couldn't save your consent right now. Please try again later.";

// The API's rule for a person's name (PersonName): 2-120 characters once trimmed.
const NAME_MIN = 2;
const NAME_MAX = 120;

export function ConsentGate({ children }: { children: React.ReactNode }) {
  const me = useQuery({ queryKey: ["me"], queryFn: getMe });
  const { data, isLoading } = useQuery({ queryKey: ["consents"], queryFn: listConsents });
  if (me.isLoading || isLoading) return <p className="p-6 text-sm text-gray-400">Loading…</p>;
  if (me.data && me.data.full_name.trim() === "") return <NameForm />;
  if (data?.some((s) => s.needs_action)) return <ConsentForm statuses={data} />;
  return <>{children}</>;
}

function NameForm() {
  const qc = useQueryClient();
  const { logout, renameUser } = useAuth();
  const [name, setName] = useState("");
  const [error, setError] = useState<string | null>(null);

  const save = useMutation({
    mutationFn: () => patchProfile({ full_name: name.trim() }),
    onMutate: () => setError(null),
    onSuccess: (me) => {
      qc.setQueryData(["me"], me);
      renameUser(me.full_name);
    },
    onError: (e) => setError(describeError(e)),
  });

  return (
    <main className="flex min-h-dvh items-start justify-center bg-gray-50 px-4 py-10 sm:items-center">
      <form
        onSubmit={(e) => {
          e.preventDefault();
          if (name.trim().length < NAME_MIN) return setError("Please enter your full name.");
          save.mutate();
        }}
        className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-sm"
      >
        <h1 className="text-xl font-semibold text-brand">What's your name?</h1>
        <p className="mt-1 text-sm text-gray-500">
          It appears on your prescriptions and orders, so your doctor and pharmacist know who they are for.
        </p>
        <label className="mt-5 flex flex-col gap-1 text-sm">
          <span className="text-gray-500">Full name</span>
          <input
            value={name}
            onChange={(e) => setName(e.target.value)}
            autoComplete="name"
            maxLength={NAME_MAX}
            required
            autoFocus
            className="w-full rounded-lg border px-3 py-2"
          />
        </label>
        {error && <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}
        <button
          type="submit"
          disabled={save.isPending}
          className="mt-5 w-full rounded-lg bg-brand py-2.5 text-sm font-medium text-brand-fg disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Continue"}
        </button>
        <button type="button" onClick={() => void logout()} className="mt-3 w-full text-center text-sm text-gray-500 hover:underline">
          Not now — sign out
        </button>
      </form>
    </main>
  );
}

function ConsentForm({ statuses }: { statuses: ConsentStatus[] }) {
  const qc = useQueryClient();
  const { logout } = useAuth();
  const [ticked, setTicked] = useState<Record<string, boolean>>({});
  const [open, setOpen] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);

  const required = statuses.filter((s) => s.needs_action);
  const optional = statuses.filter((s) => OPTIONAL_AT_SIGNUP.includes(s.consent_type) && !s.granted);
  // Accepted an earlier version of a required consent. Not "anything granted": transactional
  // messages are on for a brand-new account too (D-10).
  const returning = statuses.some((s) => s.required_version !== null && s.current_version !== null);
  const allRequired = required.every((s) => ticked[s.consent_type]);

  const save = useMutation({
    mutationFn: () =>
      grantConsents([
        ...required.map((s) => ({ consent_type: s.consent_type, consent_version: s.required_version })),
        ...optional.filter((s) => ticked[s.consent_type]).map((s) => ({ consent_type: s.consent_type })),
      ]),
    onMutate: () => setError(null),
    onSuccess: (after) => {
      if (after.some((s) => s.needs_action)) {
        setError("The consent terms were just updated. Please review them again.");
        setTicked({});
      }
      qc.setQueryData(["consents"], after);
    },
    onError: (e) =>
      setError(e instanceof ApiError && e.code === "RESOURCE_NOT_FOUND" ? SAVE_UNAVAILABLE : describeError(e)),
  });

  const item = (s: ConsentStatus) => {
    const key = s.consent_type;
    const policy = POLICY_OF[key] ? POLICIES[POLICY_OF[key]] : undefined;
    return (
      <li key={key} className="py-3">
        <label className="flex cursor-pointer items-start gap-3 text-sm">
          <input
            type="checkbox"
            className="mt-0.5 h-4 w-4 shrink-0 accent-brand"
            checked={!!ticked[key]}
            onChange={() => setTicked((t) => ({ ...t, [key]: !t[key] }))}
          />
          <span>{LABELS[key] ?? key}</span>
        </label>
        {policy && (
          <div className="ml-7 mt-1">
            <button
              type="button"
              onClick={() => setOpen(open === key ? null : key)}
              aria-expanded={open === key}
              className="text-xs font-medium text-brand hover:underline"
            >
              {open === key ? `Hide the ${policy.title}` : `Read the ${policy.title}`}
            </button>
            {open === key && (
              <div className="mt-2 space-y-2 rounded-lg bg-gray-50 p-3 text-xs text-gray-600">
                {policy.body.map((section) => (
                  <div key={section.heading}>
                    <div className="font-medium text-gray-800">{section.heading}</div>
                    <p>{section.text}</p>
                  </div>
                ))}
                <p className="text-gray-400">Updated {policy.updated}</p>
              </div>
            )}
          </div>
        )}
      </li>
    );
  };

  return (
    <main className="flex min-h-dvh items-start justify-center bg-gray-50 px-4 py-10 sm:items-center">
      <div className="w-full max-w-lg rounded-2xl bg-white p-6 shadow-sm">
        <h1 className="text-xl font-semibold text-brand">{returning ? "We've updated our terms" : "Just one more step"}</h1>
        <p className="mt-1 text-sm text-gray-500">
          {returning
            ? "Please review and accept the changes below to keep using EMediExp."
            : "Please review and accept the consents below to start using EMediExp."}
        </p>

        <h2 className="mt-5 text-xs font-semibold uppercase tracking-wide text-gray-400">Required</h2>
        <ul className="divide-y">{required.map(item)}</ul>

        {optional.length > 0 && (
          <>
            <h2 className="mt-4 text-xs font-semibold uppercase tracking-wide text-gray-400">Optional</h2>
            <ul className="divide-y">{optional.map(item)}</ul>
            <p className="mt-1 text-xs text-gray-400">You can change these later in Settings.</p>
          </>
        )}

        {error && <div className="mt-4 rounded-lg bg-red-50 px-3 py-2 text-sm text-red-700">{error}</div>}

        <button
          type="button"
          onClick={() => save.mutate()}
          disabled={!allRequired || save.isPending}
          className="mt-5 w-full rounded-lg bg-brand py-2.5 text-sm font-medium text-brand-fg disabled:opacity-50"
        >
          {save.isPending ? "Saving…" : "Agree & continue"}
        </button>
        <button type="button" onClick={() => void logout()} className="mt-3 w-full text-center text-sm text-gray-500 hover:underline">
          Not now — sign out
        </button>
      </div>
    </main>
  );
}
